import { fmtDate } from '@/shared/lib/format'
import { cls } from '@/ui/cls'
import { OUTCOMES, STAGES, type OutcomeKind } from '@/shared/domain'
import { stageLabel } from '@/shared/pipelineColors'
import type { Lead, MeetingOutcome } from '@/shared/types'
import SellerPicker from '@/features/leads/SellerPicker'
import { Eyebrow } from '@/ui'
import { useLeadWrites } from '@/features/leads/workflow/useLeadMutations'
import { useCanEdit, writeControlProps } from '@/shared/write-access'
import { Field } from './DetailField'
import DealsSection from './DealsSection'
import NextContactEditor from './NextContactEditor'

// Outcome options for a meeting that has already happened. NULL = "not
// logged yet" — surfaced as the dropdown's placeholder so an unhandled
// past meeting is visually obvious. 'rescheduled' / 'cancelled' don't
// count for or against the dashboard show rate.
const MEETING_OUTCOME_OPTIONS: { value: MeetingOutcome; label: string }[] = [
  { value: 'showed',       label: 'Dök upp' },
  { value: 'no_show',      label: 'Uteblev' },
  { value: 'rescheduled',  label: 'Ombokad' },
  { value: 'cancelled',    label: 'Inställd' },
]

interface Props {
  lead: Lead
  // Timeline event logger — marking Booked here must produce a meeting
  // event (the dashboard counts bookings from events, not stage state).
}

// Lean Details tab — sales-actionable fields only.
export default function DetailsTab({ lead }: Props) {
  const { updateLead, writeError } = useLeadWrites()
  const canEdit = useCanEdit()
  return (
    <div className="flex flex-col gap-5 p-6 max-w-[720px] mx-auto [&_section]:bg-white [&_section]:border [&_section]:border-ink-200 [&_section]:p-5">
      {writeError && <p role="alert" className="text-sm text-[var(--color-danger)]">{writeError}</p>}
      <section>
        <Eyebrow as="h4" size="sm" gap="md">Pipeline</Eyebrow>
        <Field label="Stadie">
          <select
            value={lead.stage}
            {...writeControlProps(canEdit)}
            onChange={e => {
              const next = e.target.value as Lead['stage']
                // `bookedMeeting` is already true when the booked outcome
                // logged this — that flow patches the flag and the stage
                // together. Without this guard the stage select logs a second
                // booking for the same meeting, and retracting one leaves the
                // other counting in the dashboard.
              updateLead(lead.id, { stage: next },
                next === 'Booked' && lead.stage !== 'Booked' && !lead.bookedMeeting
                  ? { kind: 'meeting', body: 'Bokat via stadieändring' } : undefined)
            }}
          >
            {STAGES.map(s => <option key={s} value={s}>{stageLabel(s)}</option>)}
          </select>
        </Field>
        <Field label="Överlämnad till">
          <SellerPicker
            value={lead.seller}
            {...writeControlProps(canEdit)}
            onChange={v => { if (v !== (lead.seller || null)) updateLead(lead.id, { seller: v }) }}
          />
        </Field>
        <Field label="Bokat möte">
          <label className={cls('inline-flex items-center gap-2', canEdit ? 'cursor-pointer' : 'cursor-not-allowed text-ink-500')}>
            <input
              type="checkbox"
              className="w-auto"
              checked={lead.bookedMeeting}
              {...writeControlProps(canEdit, 'Avmarkera om mötet aldrig bokades — drar tillbaka det från statistiken också')}
              onChange={e => {
                if ((e.nativeEvent as MouseEvent).detail > 1) return
                // Unticking is a correction, and the server treats it as one: it
                // retracts the booking entries, which is what takes the meeting
                // out of the statistics. A meeting that was booked and then fell
                // through goes in Meeting outcome instead.
                updateLead(lead.id, { bookedMeeting: e.target.checked },
                  e.target.checked && !lead.bookedMeeting
                    ? { kind: 'meeting', body: 'Markerad som bokad' } : undefined)
              }}
            />
            <span>{lead.bookedMeeting ? 'Ja' : 'Nej'}</span>
          </label>
        </Field>
        {lead.meetingAt && (
          <Field label="Mötestid">
            <span>{fmtDate(lead.meetingAt, { time: true })}</span>
          </Field>
        )}
        {lead.meetingAt && new Date(lead.meetingAt) <= new Date() && (
          <Field label="Mötesutfall">
            <select
              value={lead.meetingOutcome || ''}
              {...writeControlProps(canEdit)}
              onChange={e => {
                const v = e.target.value
                updateLead(lead.id, {
                  meetingOutcome: (v ? (v as MeetingOutcome) : null),
                })
              }}
            >
              <option value="">— inte loggat —</option>
              {MEETING_OUTCOME_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </Field>
        )}
      </section>

      <DealsSection companyId={lead.id} />

      <section>
        <Eyebrow as="h4" size="sm" gap="md">Status</Eyebrow>
        <Field label="Nästa kontakt">
          <NextContactEditor key={lead.id} lead={lead} />
        </Field>
        <Field label="Fick kontakt">
          <span>{lead.gotContact ? 'Ja' : 'Inte än'}</span>
        </Field>
      </section>

      {lead.feedback && (
        <section>
          <Eyebrow as="h4" size="sm" gap="md">Senaste utfall</Eyebrow>
          <Field label="Utfall">
            <span>
              {lead.feedback.outcome
                ? (OUTCOMES[lead.feedback.outcome as OutcomeKind]?.label ?? lead.feedback.outcome)
                : '—'}
              {' · '}{fmtDate(lead.feedback.submittedAt, { time: true })}
              {' · '}{lead.feedback.submittedBy}
            </span>
          </Field>
          {lead.feedback.rating > 0 && (
            <Field label="Betyg">
              <span>{lead.feedback.rating} / 5</span>
            </Field>
          )}
          {lead.feedback.comment && (
            <Field label="Kommentar">
              <span>{lead.feedback.comment}</span>
            </Field>
          )}
        </section>
      )}
    </div>
  )
}
